import { useCallback, useState } from 'react'
import type { UserHostDetails } from '@/infra/sse/shared/payloads'
import { useCrypto } from '@/infra/crypto/context'
import {
  buildSessionStartPayload,
  commandClient,
  encryptCommand,
} from '@/features/events/conversation/controls/api'

interface UseStartSessionReturn {
  readonly startSession: (
    workDirectory: string,
    sessionName: string,
    initialMessage: string | null,
    dangerouslySkipPermissions: boolean,
    userHostDetails: UserHostDetails,
  ) => Promise<boolean>
  readonly isSubmitting: boolean
  readonly error: string | null
}

export function useStartSession(): UseStartSessionReturn {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { encrypt, activeKeyVersion, isReady } = useCrypto()

  const startSession = useCallback(
    async (
      workDirectory: string,
      sessionName: string,
      initialMessage: string | null,
      dangerouslySkipPermissions: boolean,
      userHostDetails: UserHostDetails,
    ): Promise<boolean> => {
      if (!isReady || activeKeyVersion === null) {
        setError('Encryption is not available. Cannot start session.')
        return false
      }

      setIsSubmitting(true)
      setError(null)

      try {
        const payload = buildSessionStartPayload(
          workDirectory,
          sessionName,
          initialMessage,
          dangerouslySkipPermissions,
          userHostDetails,
        )
        const encryptedPayload = await encryptCommand(payload, activeKeyVersion, encrypt)
        const result = await commandClient.submit(encryptedPayload)

        if (!result.success) {
          setError(result.message)
          return false
        }
        return true
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An unexpected error occurred'
        setError(message)
        return false
      } finally {
        setIsSubmitting(false)
      }
    },
    [encrypt, activeKeyVersion, isReady],
  )

  return { startSession, isSubmitting, error }
}
